import { AvisoRegras, Realce, type RealceFaixa } from './regras';

export interface AvisoItem {
  readonly id: number;
  readonly code: number;
  readonly args: readonly string[];
  readonly realce: RealceFaixa;
  readonly texto: string;
  readonly rotulo: string;
  readonly icone: string;
  /** Aviso de evento: faixa própria, sai sozinho (regras.ts). */
  readonly evento: boolean;
}

export interface EstadoFila {
  ativo: AvisoItem | null;
  espera: AvisoItem[];
  vistos: { chave: string; em: number }[];
  proximoId: number;
}

export function filaVazia(): EstadoFila {
  return { ativo: null, espera: [], vistos: [], proximoId: 1 };
}

export function chaveDoAviso(code: number, args: readonly string[]): string {
  return `${code}\0${args.join('\0')}`;
}

function podarVistos(vistos: EstadoFila['vistos'], agora: number): EstadoFila['vistos'] {
  return vistos.filter(v => agora - v.em < AvisoRegras.repeticaoMs);
}

function capEspera(espera: AvisoItem[]): AvisoItem[] {
  const next = espera.slice();
  while (next.length > AvisoRegras.filaMax) {
    let pior = 0;
    for (let i = 1; i < next.length; i++) {
      const a = next[i];
      const b = next[pior];
      if (a.realce < b.realce || (a.realce === b.realce && a.id < b.id)) pior = i;
    }
    next.splice(pior, 1);
  }
  return next;
}

export function receberAviso(
  estado: EstadoFila,
  peca: Omit<AvisoItem, 'id'>,
  agora: number,
  mostrarNaTela: boolean
): { estado: EstadoFila; som: boolean } {
  if (!mostrarNaTela && peca.realce !== Realce.Urgente) {
    return { estado, som: false };
  }

  const chave = chaveDoAviso(peca.code, peca.args);
  const vistos = podarVistos(estado.vistos, agora);
  if (vistos.some(v => v.chave === chave)) {
    return { estado: { ...estado, vistos }, som: false };
  }
  vistos.push({ chave, em: agora });

  const item: AvisoItem = { ...peca, id: estado.proximoId };
  const proximoId = estado.proximoId + 1;

  if (!estado.ativo) {
    return {
      estado: { ativo: item, espera: estado.espera, vistos, proximoId },
      som: peca.realce === Realce.Urgente && AvisoRegras.somUrgente
    };
  }

  if (peca.realce === Realce.Urgente && estado.ativo.realce !== Realce.Urgente) {
    return {
      estado: {
        ativo: item,
        espera: capEspera([estado.ativo, ...estado.espera]),
        vistos,
        proximoId
      },
      som: AvisoRegras.somUrgente
    };
  }

  return {
    estado: {
      ativo: estado.ativo,
      espera: capEspera([...estado.espera, item]),
      vistos,
      proximoId
    },
    som: false
  };
}

export function avancarFila(estado: EstadoFila): { estado: EstadoFila; som: boolean } {
  const [proximo, ...resto] = estado.espera;
  return {
    estado: { ...estado, ativo: proximo ?? null, espera: resto },
    som: !!proximo && proximo.realce === Realce.Urgente && AvisoRegras.somUrgente
  };
}
